import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import {
  getHrWingConfig,
  saveHrWingConfig,
  HrWingConfig,
} from "../eSepration/services/eSeparation.service";
import { isSupportViewer } from "../../../utils/permissionUtils";

const padEmpNo = (v?: string | null): string => {
  const t = (v ?? "").trim();
  return t ? t.padStart(8, "0") : "";
};

const EsepHrWingSettings: React.FC = () => {
  const routes = all_routes;

  const [wings, setWings] = useState<HrWingConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<{ wing: string; hr_approver_emp_id: string }>(
    { wing: "", hr_approver_emp_id: "" }
  );
  const [saving, setSaving] = useState(false);

  const loadWings = async () => {
    setLoading(true);
    const res = await getHrWingConfig();
    setWings(res.data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadWings();
  }, []);

  const saveWing = async () => {
    if (!form.wing.trim() || !form.hr_approver_emp_id.trim())
      return toast.warning("Wing and HR approver employee ID are required.");
    setSaving(true);
    const res = await saveHrWingConfig({
      wing: form.wing.trim(),
      hr_approver_emp_id: padEmpNo(form.hr_approver_emp_id),
    });
    setSaving(false);
    if (res.success) {
      toast.success("HR wing mapping saved.");
      setForm({ wing: "", hr_approver_emp_id: "" });
      loadWings();
    } else {
      toast.error(res.message || "Failed to save HR wing mapping.");
    }
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Settings</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">App Settings</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    E-Separation HR Wing Mapping
                  </li>
                </ol>
              </nav>
            </div>
            <div className="head-icons ms-2">
              <CollapseHeader />
            </div>
          </div>

          <div className="row">
            {/* Existing mappings */}
            <div className={isSupportViewer() ? "col-xl-12" : "col-xl-7"}>
              <div className="card">
                <div className="card-body">
                  <div className="border-bottom mb-3 pb-3 d-flex align-items-center justify-content-between">
                    <h4 className="mb-0">HR Wing Mapping</h4>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={loadWings}
                      disabled={loading}
                    >
                      <i className="ti ti-refresh me-1" />
                      Refresh
                    </button>
                  </div>

                  <p className="text-muted">
                    The HR wing mapping decides which HR approver handles each
                    wing&apos;s e-separations.
                  </p>

                  {loading ? (
                    <div className="text-center py-4 text-muted">
                      <div className="spinner-border text-primary mb-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="small">Loading mappings...</div>
                    </div>
                  ) : wings.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="ti ti-building-off fs-3 d-block mb-2" />
                      No HR wing mappings configured yet.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Wing</th>
                            <th className="text-end">HR Approver Emp. ID</th>
                          </tr>
                        </thead>
                          <tbody>
                          {wings.map((w, i) => (
                            <tr key={i}>
                              <td className="fw-medium">{w.wing}</td>
                              <td className="text-end text-muted">
                                {w.hr_approver_emp_id}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Add / update mapping */}
            {!isSupportViewer() && (
              <div className="col-xl-5">
                <div className="card">
                  <div className="card-body">
                    <div className="border-bottom mb-3 pb-3">
                      <h4 className="mb-0">Add / Update Mapping</h4>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Wing <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        value={form.wing}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, wing: e.target.value }))
                        }
                        placeholder="e.g. Signal & Telecom"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        HR Approver Employee ID{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        inputMode="numeric"
                        value={form.hr_approver_emp_id}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            hr_approver_emp_id: e.target.value,
                          }))
                        }
                        onBlur={(e) =>
                          setForm((f) => ({
                            ...f,
                            hr_approver_emp_id: e.target.value.trim()
                              ? padEmpNo(e.target.value)
                              : "",
                          }))
                        }
                        placeholder="8-digit employee number"
                      />
                    </div>

                    <div className="d-flex align-items-center justify-content-end">
                      <button
                        type="button"
                        className="btn btn-outline-light border me-3"
                        onClick={() =>
                          setForm({ wing: "", hr_approver_emp_id: "" })
                        }
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={saveWing}
                        disabled={saving}
                      >
                        <i className="ti ti-device-floppy me-1" />
                        {saving ? "Saving..." : "Save Mapping"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2014 - 2025 © SmartHR.</p>
        </div>
      </div>
    </div>
  );
};

export default EsepHrWingSettings;
